// Presence helpers — `types-and-schemas.md` §4.2 `PresencePayload`.

import type { ChatPresence } from '../chat/ChatRoom.types';
import type { PresenceState } from './types';

/** Decay peer presence to offline when no frame received within this window. */
export const PRESENCE_STALE_MS = 90_000;

const PRESENCE_RANK: Record<Exclude<ChatPresence, null>, number> = {
  online: 3,
  away: 2,
  offline: 1,
};

export function parsePresencePayload(payload: unknown): PresenceState | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const state = (payload as { state?: unknown }).state;
  if (
    state === 'online' ||
    state === 'away' ||
    state === 'dnd' ||
    state === 'offline'
  ) {
    return state;
  }
  return null;
}

/** Map wire presence to chat header / avatar shape (`dnd` → `away`). */
export function presenceToChatPresence(state: PresenceState): ChatPresence {
  if (state === 'dnd') return 'away';
  return state;
}

export function mergeChatPresence(
  current: ChatPresence,
  next: ChatPresence,
): ChatPresence {
  if (current === null) return next;
  if (next === null) return current;
  return PRESENCE_RANK[next] >= PRESENCE_RANK[current] ? next : current;
}

export interface PeerPresenceEntry {
  readonly deviceKey: string;
  readonly presence: ChatPresence;
  readonly updatedAtMs: number;
}

/** Pick strongest presence across peer devices (multi-device readers). */
export function aggregatePeerPresence(
  entries: ReadonlyArray<PeerPresenceEntry>,
  nowMs: number = Date.now(),
): ChatPresence {
  const cutoff = nowMs - PRESENCE_STALE_MS;
  let merged: ChatPresence = null;
  let sawFresh = false;
  for (const entry of entries) {
    if (entry.updatedAtMs <= cutoff) continue;
    sawFresh = true;
    merged = mergeChatPresence(merged, entry.presence);
  }
  if (!sawFresh) return null;
  return merged ?? 'offline';
}
